import { Dialog } from "components/Dialog";
import { TextField } from "components/TextField";
import { styled } from "@mui/material/styles";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ChartListItem } from "interfaces/chart";

export interface SaveChartDialogProps {
  open: boolean;
  loading?: boolean;
  savedCharts: ChartListItem[];
  initialName: string | undefined;
  error?: boolean;
  errorText?: string;
  onClose: () => void;
  onSave: (chartName: string) => void;
}

export const SaveChartDialog = ({
  open,
  loading = false,
  initialName,
  error = false,
  savedCharts,
  errorText = "An error occurred while trying to save the chart.",
  onSave,
  onClose
}: SaveChartDialogProps): JSX.Element => {
  const validationSchema = Yup.object({
    chartName: Yup.string()
      .label("Name")
      .trim()
      .required()
      .max(50, "Name cannot be longer than 50 characters")
      .test("isUnique", "A chart with this name already exists.", value => {
        return !savedCharts.some(chart => chart.chart_name === value);
      })
  });
  const formik = useFormik({
    initialValues: {
      chartName: initialName ?? ""
    },
    validateOnMount: true,
    onSubmit: values => {
      onSave(values.chartName);
    },
    validationSchema
  });

  return (
    <Dialog
      dialogId="dialog-save-chart"
      open={open}
      actionsDisabled={loading}
      title="Save Chart"
      confirmText="Save Chart"
      confirmDisabled={!formik.isValid}
      errorText={error ? errorText : undefined}
      onSubmit={formik.handleSubmit}
      onClose={onClose}
    >
      <ContentWrapper>
        {/* TODO: re-add when workspace available */}
        {/*<SecondaryText variant="body1">*/}
        {/*  Save your chart to reuse here or on a new worksheet.*/}
        {/*</SecondaryText>*/}
        {/*<TipText variant="caption">*/}
        {/*  Tip: Find your saved charts on the configure panel of the chart*/}
        {/*  editor.*/}
        {/*</TipText>*/}
        <TextField
          id="chartName"
          size="small"
          fullWidth
          label="Name"
          required
          autoFocus
          value={formik.values.chartName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.chartName && Boolean(formik.errors.chartName)}
          helperText={formik.touched.chartName && formik.errors.chartName}
        />
      </ContentWrapper>
    </Dialog>
  );
};

const ContentWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing(2)};
`;

// TODO: re-enable when workspace available
// const SecondaryText = styled(Typography)`
//   font-style: normal;
//   font-weight: 400;
//   font-size: 16px;
//   line-height: 24px;
//   letter-spacing: 0.15px;
//   color: rgba(0, 0, 0, 0.6);
// `;
//
// const TipText = styled(Typography)`
//   font-weight: 400;
//   font-size: 12px;
//   line-height: 20px;
//   letter-spacing: 0.4px;
//   color: rgba(0, 0, 0, 0.6);
// `;
